import React, { Suspense } from 'react'
import { isMIUI } from 'react-device-detect'
import { Redirect, Switch, useLocation } from 'react-router-dom'
import AuthRoutes from '@/modules/Auth/auth-routes'
import MedicalSpecialitiesRoutes from '@/modules/MedicalSpecialities/routes'
import MyCalllsRoutes from '@/modules/MyCalls/my-calls-routes'
import ScreeningRoutes from '@/modules/Screening/screening-routes'
import MyCardsRoutes from '@/modules/MyCards/routes'
import ProfileRoutes from '@/modules/Profile/profile-routes'
import ScheduledRoutes from '@/modules/Attendance/submodules/Scheduled/scheduled-routes'
import AttachmentRoutes from '@/modules/Attachment/attachment-routes'
import ScheduledPsyAliveRoutes from '@/modules/Attendance/submodules/ScheduledPsyAlive/routes'
import ImmediateRoutes from '@/modules/Attendance/submodules/Immediate/immediate-routes'
import ScheduledWithoutPreferenceRoutes from '@/modules/Attendance/submodules/ScheduledWithoutPreference/scheduled-w-routes'
import CheckoutRoutes from '@/modules/Checkout/checkout-routes'
import RequireAuthentication from '@/router/guards/RequireAuthentication'
import Navbar from '@/components/Navbar'
import LoadingPage from '@/components/LoadingPage'
import useRealtimeAttendance from '@/modules/RealtimeAttendance/useRealtimeAttendance'
import ConfirmContactRoutes from '@/modules/Attendance/submodules/ConfirmContact/confirm-contact-routes'
import NPSRoutes from '@/modules/NPS/routes'
import VideoCallRoutes from '@/modules/VideoCall/video-call-routes'
import StaticPagesRoutes from '@/modules/StaticPages/static-pages-routes'
import PatientRegistrationRoutes from '@/modules/Signup/routes'
import MyBenefitsRoutes from '@/modules/Benefits/benefits-routes'
import MyConexaPlusRoutes from '@/modules/ConexaPlus/routes'
import ActivationRoutes from '@/modules/Activation/routes'
import ScrollToTop from './ScrollToTop'
import HealthTeamsRoutes from '@/modules/HealthTeams/routes'
import ExamsPartnersRoutes from '@/modules/ExamsPartners/routes'
import BeepRoutes from '@/modules/Beep/routes'
import BetiRoutes from '@/modules/Beti/routes'
import CareRoutes from '@/modules/Care/routes/routes'
import YourHealthRoutes from '@/modules/YourHealth/routes'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { getCache } from '@/utils/cache'
import { AUTHENTICATED_2FA_TIME } from '@/modules/Auth/pages/TwoFactorAuth/constants'
import { LOGGED_IN_WITH_MAGIC_LINK } from '@/modules/Auth/pages/MagicLink/consts'
import { RenderIf } from '@/components/RenderIf'
import { IncompatibleBrowserWarning } from '@/components/IncompatibleBrowserWarning'
import { FunctionComponentChildren } from '@/@types/react'
import {
  ROUTES_WITHOUT_AVATAR,
  ROUTES_WITHOUT_MENU_OPTIONS,
  ROUTES_WITHOUT_NAVBAR,
  ROUTES_WITH_NAVBAR_EXCEPTIONS,
} from './constants'
import MinorGuardianConsentRoutes from '@/modules/MinorGuardianConsent/routes'
import RoomRoutes from '@/modules/Attendance/submodules/Room/room-routes'
import { roomPaths } from '@/modules/Attendance/submodules/Room/constants'

const Router: FunctionComponentChildren = props => {
  const location = useLocation()
  const { clinic } = useAuthContext()
  useRealtimeAttendance()

  const isRouteWithoutNavbar =
    ROUTES_WITHOUT_NAVBAR?.map(route =>
      location?.pathname?.includes(route),
    ).some(route => route) &&
    !ROUTES_WITH_NAVBAR_EXCEPTIONS.includes(location.pathname)

  const isRouteWithoutMenuOptions =
    ROUTES_WITHOUT_MENU_OPTIONS?.map(route =>
      location?.pathname?.includes(route),
    ).some(route => route) && !location?.pathname?.includes(roomPaths.basePath)

  const isRouteWithAvatar = !ROUTES_WITHOUT_AVATAR.some(route =>
    location?.pathname?.includes(route),
  )

  const shouldUseTwoFactorAuth =
    clinic?.flag?.HabilitarAutenticacaoDoisFatoresLoginPaciente &&
    !getCache(AUTHENTICATED_2FA_TIME) &&
    !getCache(LOGGED_IN_WITH_MAGIC_LINK)

  return (
    <Suspense fallback={<LoadingPage withoutNav={isRouteWithoutNavbar} />}>
      <RenderIf condition={isMIUI}>
        <IncompatibleBrowserWarning />
      </RenderIf>

      {!isRouteWithoutNavbar ? (
        <Navbar
          hideMenuOptions={isRouteWithoutMenuOptions}
          showAvatar={isRouteWithAvatar}
        />
      ) : null}

      {shouldUseTwoFactorAuth ? (
        <Redirect to="/autenticacao-dois-fatores" />
      ) : null}

      <ScrollToTop>
        <Switch {...props}>
          {CareRoutes}
          {MedicalSpecialitiesRoutes}
          {AuthRoutes}
          {MyCalllsRoutes}
          {ScreeningRoutes}
          {MyCardsRoutes}
          {ProfileRoutes}
          {ImmediateRoutes}
          {ScheduledWithoutPreferenceRoutes}
          {CheckoutRoutes}
          {ConfirmContactRoutes}
          <RequireAuthentication path="/avaliacao" component={NPSRoutes} />
          {VideoCallRoutes}
          {StaticPagesRoutes}
          {PatientRegistrationRoutes}
          <RequireAuthentication
            path="/atendimento-agendado-psicologo"
            component={ScheduledPsyAliveRoutes}
          />
          <RequireAuthentication
            path="/atendimento-agendado"
            component={ScheduledRoutes}
          />
          <RequireAuthentication
            path="/sala-atendimento"
            component={RoomRoutes}
          />
          <RequireAuthentication
            path="/documentos"
            component={AttachmentRoutes}
          />
          {MyBenefitsRoutes}
          {MyConexaPlusRoutes}
          {BetiRoutes}
          {ActivationRoutes}
          {YourHealthRoutes}
          {HealthTeamsRoutes}
          {ExamsPartnersRoutes}
          {BeepRoutes}
          {MinorGuardianConsentRoutes}
          <Redirect to="/" />
        </Switch>
      </ScrollToTop>
    </Suspense>
  )
}

export default Router
